// src/pos-app/surfaces/waiter/TablePopover.js
//
// F12a-N Task W2 — the table-action popover (design contract:
// docs/mockups/f12a-n/waiter-tables.html .pop / popContent() L1052-1104).
// Renders at a pre-clamped {left,top} the caller supplies — FloorCanvas owns
// the tap-position math against its own canvas box (see that file's
// header). Status-dependent action sets are a faithful extraction of
// TableServicePage.js:958-1169 (popover body per tablePopover.status),
// wired to the handlers at :365-610 by the caller — this component only
// renders the buttons and forwards taps; every service call and the
// merge/change-table mode transitions live in TablesSurface /
// lib/tableFlow.js.
//
// One documented convergence: `table.status` arrives already derived by the
// SAME deriveTableStatus source FloorCanvas's tiles use, rather than the
// legacy popover's own slightly different inline computation
// (TableServicePage.js:345-353, which — per the parity inventory's
// "duplicated status-derivation... must stay in sync" flag — omits the
// `cleared_at` / `occ.status==='active'` checks VisualTableSelector
// applies). Using one function for both surfaces resolves that drift
// instead of reproducing it a third time.
//
// The mockup shows the SERVED chip in the popover title for any occupation
// with `served_at` set (not gated to the 'ordered' status the way legacy's
// inline block was) — a strict superset of legacy's display, kept per the
// binding design contract.
import React from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const CSS = `
.wtb-pop{position:absolute;z-index:20;width:236px;background:var(--c-surface);border:1px solid var(--c-border);
  border-radius:15px;box-shadow:0 20px 50px rgba(2,8,23,.22);padding:12px;font-family:inherit;}
.wtb-pop-head{display:flex;align-items:flex-start;gap:8px;margin-bottom:2px;}
.wtb-pop-title{flex:1;min-width:0;display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:14.5px;font-weight:800;color:var(--c-ink);}
.wtb-pop-close{flex:none;width:24px;height:24px;border-radius:8px;display:flex;align-items:center;justify-content:center;
  background:none;border:none;color:var(--c-muted);cursor:pointer;font-size:15px;line-height:1;padding:0;}
.wtb-pop-close:hover{color:var(--c-red);}
.wtb-pop-stat{display:inline-flex;align-items:center;gap:5px;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;}
.wtb-pop-stat i{width:8px;height:8px;border-radius:50%;flex:none;}
.wtb-pop-served{font-size:8.5px;font-weight:800;letter-spacing:.5px;padding:1px 6px;border-radius:6px;
  color:var(--c-success);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);}
.wtb-pop-sub{font-size:11px;color:var(--c-muted);font-weight:600;margin-bottom:10px;}
.wtb-pa{display:flex;align-items:center;gap:9px;width:100%;min-height:40px;padding:8px 10px;border:none;
  border-radius:10px;font-size:13px;font-weight:700;color:var(--c-ink);text-align:start;cursor:pointer;
  background:none;font-family:inherit;margin-bottom:4px;}
.wtb-pa svg{color:var(--c-muted);flex:none;}
.wtb-pa:hover{background:var(--c-greenLight);}
.wtb-pa.primary{background:var(--c-green);color:#fff;justify-content:center;margin-bottom:6px;}
.wtb-pa.primary svg{color:#fff;}
.wtb-pa.primary:hover{background:var(--c-greenDark);}
.wtb-pa.destructive{color:var(--c-red);}
.wtb-pa.destructive svg{color:var(--c-red);}
.wtb-pa.destructive:hover{background:var(--c-redLight);}
.wtb-pop-div{height:1px;background:var(--c-border);margin:5px 2px;}
.wtb-party-stepper{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:7px 8px;
  margin-bottom:9px;border:1px solid var(--c-border);border-radius:12px;background:var(--c-faint);}
.wtb-party-stepper .lbl{font-size:11px;font-weight:700;color:var(--c-muted);text-transform:uppercase;letter-spacing:.5px;}
.wtb-party-stepper .ctrl{display:inline-flex;align-items:center;gap:2px;}
.wtb-party-stepper button{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  color:var(--c-slate);background:var(--c-surface);border:1px solid var(--c-border);cursor:pointer;font-size:15px;font-weight:800;
  padding:0;font-family:inherit;}
.wtb-party-stepper button:hover{color:var(--c-green);border-color:var(--c-greenBorder);}
.wtb-party-stepper .q{min-width:26px;text-align:center;font-size:14px;font-weight:800;}
`;

const icon = (d) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={d} />
    </svg>
);

const ICONS = {
    order: 'M12 3v18M3 12h18',
    move: 'M5 9l-3 3 3 3M19 9l3 3-3 3M2 12h20',
    split: 'M16 3h5v5M8 3H3v5M21 3l-7.5 7.5M3 3l7.5 7.5M12 12v9',
    clear: 'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6',
    bill: 'M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2Z M8 12h8',
    served: 'M20 6 9 17l-5-5',
    add: 'M12 5v14M5 12h14',
};

export default function TablePopover({
    table,
    partySize,
    onPartySizeChange,
    onClose,
    onSeatWalkIn,
    onNewOrder,
    onAddItems,
    onMarkServed,
    onRequestBill,
    onChangeTable,
    onSplitGroup,
    onClearTable,
}) {
    const { t } = useLanguage();
    if (!table) return null;
    const { tableNumber, status, occupation, capacity, isGroupAnchor, left, top } = table;

    const STATUS_COLOR = {
        available: 'var(--c-success)',
        seated: 'var(--c-blue)',
        ordered: 'var(--c-red)',
        awaiting_bill: 'var(--c-amber)',
        paid: 'var(--c-mutedLight)',
    }[status];

    const statusLabel = {
        available: t('available'),
        seated: t('seated'),
        ordered: t('ordered', {}, 'Ordered'),
        awaiting_bill: t('awaiting_bill'),
        paid: t('paid'),
    }[status];

    return (
        <div
            className="wtb-pop"
            style={{ left, top }}
            role="dialog"
            aria-label={`${t('table')} ${tableNumber}`}
            onClick={(e) => e.stopPropagation()}
        >
            <style>{CSS}</style>

            <div className="wtb-pop-head">
                <div className="wtb-pop-title">
                    <span>{t('table')} {tableNumber}</span>
                    <span className="wtb-pop-stat" style={{ color: STATUS_COLOR }}>
                        <i style={{ background: STATUS_COLOR }} />
                        {statusLabel}
                    </span>
                    {occupation?.served_at && <span className="wtb-pop-served">{t('served')}</span>}
                </div>
                <button type="button" className="wtb-pop-close" onClick={onClose} aria-label={t('close', {}, 'Close')}>
                    ×
                </button>
            </div>

            <div className="wtb-pop-sub">
                {status === 'available'
                    ? `${t('seats', {}, 'Seats')} ${capacity}`
                    : `${occupation ? occupation.party_size : '—'}/${capacity} ${t('guests', {}, 'guests')}`}
            </div>

            {status === 'available' && (
                <>
                    <div className="wtb-party-stepper">
                        <span className="lbl">{t('party_size')}</span>
                        <span className="ctrl">
                            <button
                                type="button"
                                data-act="pminus"
                                onClick={() => onPartySizeChange(Math.max(1, partySize - 1))}
                                aria-label={t('decrease_party_size', {}, 'Decrease party size')}
                            >
                                −
                            </button>
                            <span className="q">{partySize}</span>
                            <button
                                type="button"
                                data-act="pplus"
                                onClick={() => onPartySizeChange(partySize + 1)}
                                aria-label={t('increase_party_size', {}, 'Increase party size')}
                            >
                                +
                            </button>
                        </span>
                    </div>
                    <button type="button" className="wtb-pa primary" data-act="seat" onClick={onSeatWalkIn}>
                        {t('seat_walk_in')}
                    </button>
                </>
            )}

            {status === 'seated' && (
                <>
                    <button type="button" className="wtb-pa primary" data-act="neworder" onClick={onNewOrder}>
                        {icon(ICONS.order)}{t('new_order')}
                    </button>
                    <button type="button" className="wtb-pa" data-act="change" onClick={onChangeTable}>
                        {icon(ICONS.move)}{t('change_table')}
                    </button>
                    {isGroupAnchor && (
                        <button type="button" className="wtb-pa" data-act="splitgrp" onClick={onSplitGroup}>
                            {icon(ICONS.split)}{t('split_group')}
                        </button>
                    )}
                    <div className="wtb-pop-div" />
                    <button type="button" className="wtb-pa destructive" data-act="clear" onClick={onClearTable}>
                        {icon(ICONS.clear)}{t('clear_table')}
                    </button>
                </>
            )}

            {status === 'ordered' && (
                <>
                    <button type="button" className="wtb-pa primary" data-act="additems" onClick={onAddItems}>
                        {icon(ICONS.add)}{t('add_items')}
                    </button>
                    {!occupation?.served_at && (
                        <button type="button" className="wtb-pa" data-act="markserved" onClick={onMarkServed}>
                            {icon(ICONS.served)}{t('mark_served')}
                        </button>
                    )}
                    <button type="button" className="wtb-pa" data-act="reqbill" onClick={onRequestBill}>
                        {icon(ICONS.bill)}{t('request_bill')}
                    </button>
                    <button type="button" className="wtb-pa" data-act="change" onClick={onChangeTable}>
                        {icon(ICONS.move)}{t('change_table')}
                    </button>
                    {isGroupAnchor && (
                        <button type="button" className="wtb-pa" data-act="splitgrp" onClick={onSplitGroup}>
                            {icon(ICONS.split)}{t('split_group')}
                        </button>
                    )}
                    <div className="wtb-pop-div" />
                    <button type="button" className="wtb-pa destructive" data-act="clear" onClick={onClearTable}>
                        {icon(ICONS.clear)}{t('clear_table')}
                    </button>
                </>
            )}

            {(status === 'awaiting_bill' || status === 'paid') && (
                <button type="button" className="wtb-pa destructive" data-act="clear" onClick={onClearTable}>
                    {icon(ICONS.clear)}{t('clear_table')}
                </button>
            )}
        </div>
    );
}
